import { Inject, Injectable, InjectionToken, Optional, PLATFORM_ID } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import type { ArticlesPartialState } from './articles.reducer';
import { ArticlesService } from '../services/articles.service';
import { distinctUntilChanged, filter, map, mergeMap, skipWhile, withLatestFrom } from 'rxjs/operators';
import type { RouterNavigatedAction } from '@ngrx/router-store';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import type { Params } from '@angular/router';
import {
  loadArticle,
  loadArticleFailure,
  loadArticleSuccess,
  refreshArticle,
  selectArticle,
  unselectArticle,
} from './articles.actions';
import { ARTICLE_ID_PARAM_KEY } from './articles.models';
import type { NmoBaseRouterStoreState } from '@candyland/angular/shared/data-access-nmo';
import { of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { selectCurrentArticle } from './articles.selectors';
import { sharedAPIErrorParser } from '@candyland/angular/shared/util-nmo';
import { isPlatformBrowser } from '@angular/common';
import type { ArticleResponse } from '@candyland/generic/shared/article-tt-com/model';

export const IS_GAA_FUNC = new InjectionToken('Showcase EA isGaa');

@Injectable()
export class ArticlesEffects {
  navigationEventSplitter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      map((r: RouterNavigatedAction<NmoBaseRouterStoreState>): { articleId: string; isGaa: boolean } => ({
        articleId: r.payload.routerState.mergedParams[ARTICLE_ID_PARAM_KEY],
        isGaa:
          isPlatformBrowser(this.platformId) && this.isGaa ? this.isGaa(r.payload.routerState.queryParams) : undefined,
      })),
      distinctUntilChanged(
        (x: { articleId: string; isGaa: boolean }, y: { articleId: string; isGaa: boolean }): boolean =>
          x.articleId === y.articleId
      ),
      skipWhile(({ articleId }): boolean => !articleId),
      mergeMap(({ articleId: id, isGaa }) =>
        !id ? of(unselectArticle()) : of(selectArticle({ id }), loadArticle({ id, isGaa }))
      )
    )
  );

  loadArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadArticle),
      fetch({
        id: (action: ReturnType<typeof loadArticle>) => action.id,

        run: (action: ReturnType<typeof loadArticle>) =>
          this.articlesService
            .getArticle(action.id)
            .pipe(
              map((article: ArticleResponse) => loadArticleSuccess({ article, id: action.id, isGaa: action.isGaa }))
            ),
        onError: (action: ReturnType<typeof loadArticle>, err) => {
          console.error('loadArticle$::apiCall', err);
          return loadArticleFailure({ id: action.id, error: sharedAPIErrorParser(err) });
        },
      })
    )
  );

  refreshArticle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshArticle),
      withLatestFrom(this.articleStore.pipe(select(selectCurrentArticle))),
      filter(([action, article]) => !!article),
      map(([action, article]) => loadArticle({ id: article.id }))
    )
  );

  constructor(
    private actions$: Actions,
    private articlesService: ArticlesService,
    private articleStore: Store<ArticlesPartialState>,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(IS_GAA_FUNC) private isGaa: (params: Params) => boolean = () => undefined
  ) {}
}
